import { useState, useMemo } from 'react';
import { Modal, Upload, Table, Steps, Button, Alert, Progress, Typography, Space, Tag, Tooltip, message } from 'antd';
import {
  UploadOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { UserImportRow, UserImportResponse, ImportRowResult } from '@mygate/common';
import { usersService } from '@/services/users';
import { parseCSV, mapCSVToUsers, validateAllRows, readFileAsText } from '@/utils/csvParser';

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface ImportUsersModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

export function ImportUsersModal({ open, onClose, onSuccess }: ImportUsersModalProps) {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedRows, setParsedRows] = useState<UserImportRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<Map<number, string[]>>(new Map());
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<UserImportResponse | null>(null);

  const stepIndex = useMemo(() => {
    switch (currentStep) {
      case 'upload':
        return 0;
      case 'preview':
        return 1;
      case 'importing':
        return 2;
      case 'results':
        return 3;
      default:
        return 0;
    }
  }, [currentStep]);

  const validRowsCount = useMemo(() => {
    return parsedRows.length - validationErrors.size;
  }, [parsedRows.length, validationErrors.size]);

  const resetState = () => {
    setCurrentStep('upload');
    setFileList([]);
    setParsedRows([]);
    setValidationErrors(new Map());
    setParseError(null);
    setImporting(false);
    setImportProgress(0);
    setImportResults(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileUpload = async (file: File) => {
    try {
      setParseError(null);
      const content = await readFileAsText(file);
      const rawData = parseCSV(content);
      const result = mapCSVToUsers(rawData);

      if (result.errors.length > 0) {
        setParseError(result.errors.map(e => e.message).join(', '));
        return false;
      }

      if (result.rows.length === 0) {
        setParseError('No data rows found in CSV');
        return false;
      }

      // Validate all rows
      const errors = validateAllRows(result.rows);

      setParsedRows(result.rows);
      setValidationErrors(errors);
      setCurrentStep('preview');
      return false; // Prevent antd from uploading
    } catch (error) {
      setParseError((error as Error).message || 'Failed to parse CSV file');
      return false;
    }
  };

  const handleImport = async () => {
    if (validRowsCount === 0) {
      message.error('No valid rows to import');
      return;
    }

    // Filter out invalid rows
    const validRows = parsedRows.filter((_, index) => !validationErrors.has(index));

    setImporting(true);
    setCurrentStep('importing');
    setImportProgress(10);

    try {
      setImportProgress(30);
      const response = await usersService.importUsers({ users: validRows });
      setImportProgress(100);

      if (response.success && response.data) {
        setImportResults(response.data);
        setCurrentStep('results');

        if (response.data.imported > 0) {
          message.success(`Successfully imported ${response.data.imported} users`);
        }
      } else {
        message.error('Import failed');
        setCurrentStep('preview');
      }
    } catch (error) {
      message.error((error as Error).message || 'Import failed');
      setCurrentStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const handleDone = () => {
    if (importResults && importResults.imported > 0) {
      onSuccess();
    }
    handleClose();
  };

  const previewColumns = [
    {
      title: '#',
      key: 'row',
      width: 50,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: (_: unknown, __: unknown, index: number) => {
        const errors = validationErrors.get(index);
        if (errors && errors.length > 0) {
          return (
            <Tooltip title={errors.join(', ')}>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                Invalid
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Valid
          </Tag>
        );
      },
    },
    {
      title: 'Name',
      key: 'name',
      render: (row: UserImportRow) => `${row.firstName} ${row.lastName}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Language',
      dataIndex: 'preferredLanguage',
      key: 'preferredLanguage',
      width: 80,
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      ellipsis: true,
    },
    {
      title: 'Wallets',
      key: 'wallets',
      width: 80,
      render: (row: UserImportRow) => row.tanganyWallets?.length || 0,
    },
  ];

  const resultsColumns = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 60,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        switch (status) {
          case 'imported':
            return (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Imported
              </Tag>
            );
          case 'skipped':
            return (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                Skipped
              </Tag>
            );
          case 'failed':
            return (
              <Tag color="error" icon={<CloseCircleOutlined />}>
                Failed
              </Tag>
            );
          default:
            return <Tag>{status}</Tag>;
        }
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Details',
      key: 'details',
      render: (result: ImportRowResult) => {
        if (result.reason === 'DUPLICATE_EMAIL') {
          return <Text type="secondary">Duplicate email</Text>;
        }
        if (result.reason === 'DUPLICATE_PHONE') {
          return <Text type="secondary">Duplicate phone</Text>;
        }
        if (result.reason === 'DUPLICATE_BOTH') {
          return <Text type="secondary">Duplicate email and phone</Text>;
        }
        if (result.errors && result.errors.length > 0) {
          return (
            <Tooltip title={result.errors.join(', ')}>
              <Text type="danger">{result.errors[0]}</Text>
            </Tooltip>
          );
        }
        if (result.userId) {
          return <Text type="secondary">ID: {result.userId}</Text>;
        }
        return null;
      },
    },
  ];

  const renderUploadStep = () => (
    <div style={{ padding: '24px 0' }}>
      <Dragger
        accept=".csv"
        fileList={fileList}
        beforeUpload={handleFileUpload}
        onChange={info => setFileList(info.fileList.slice(-1))}
        maxCount={1}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag CSV file to this area</p>
        <p className="ant-upload-hint">
          Upload a CSV file with user data. The file must contain columns: Firstname, Lastname, Email, Mobile validated
        </p>
      </Dragger>

      {parseError && (
        <Alert message="Parse Error" description={parseError} type="error" showIcon style={{ marginTop: 16 }} />
      )}

      <Alert
        message="Expected CSV Format"
        description={
          <div>
            <Text>Required columns: Firstname, Lastname, Email, Mobile validated</Text>
            <br />
            <Text>
              Optional columns: Language, Wallet Keys, Street, Street Number, ZIP, City, Country Alpha2, BD (birth date)
            </Text>
            <br />
            <Text type="secondary">Phone numbers must be in E.164 format (e.g., +491234567890)</Text>
          </div>
        }
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </div>
  );

  const renderPreviewStep = () => (
    <div style={{ padding: '16px 0' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Text strong>Total rows: {parsedRows.length}</Text>
            <Tag color="success">{validRowsCount} valid</Tag>
            {validationErrors.size > 0 && (
              <Tag color="error" icon={<WarningOutlined />}>
                {validationErrors.size} invalid
              </Tag>
            )}
          </Space>
        </div>

        {validationErrors.size > 0 && (
          <Alert
            message={`${validationErrors.size} rows have validation errors and will be skipped`}
            type="warning"
            showIcon
          />
        )}

        <Table
          dataSource={parsedRows}
          columns={previewColumns}
          rowKey={(_, index) => String(index)}
          size="small"
          scroll={{ y: 300 }}
          pagination={false}
          rowClassName={(_, index) => (validationErrors.has(index) ? 'ant-table-row-error' : '')}
        />
      </Space>
    </div>
  );

  const renderImportingStep = () => (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <Progress type="circle" percent={importProgress} status={importing ? 'active' : undefined} />
      <Title level={4} style={{ marginTop: 24 }}>
        Importing users...
      </Title>
      <Text type="secondary">Please wait while we import {validRowsCount} users</Text>
    </div>
  );

  const renderResultsStep = () => {
    if (!importResults) return null;

    return (
      <div style={{ padding: '16px 0' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ color: '#52c41a', margin: 0 }}>
                {importResults.imported}
              </Title>
              <Text>Imported</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ color: '#faad14', margin: 0 }}>
                {importResults.skipped}
              </Title>
              <Text>Skipped</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ color: '#ff4d4f', margin: 0 }}>
                {importResults.failed}
              </Title>
              <Text>Failed</Text>
            </div>
          </div>

          {(importResults.skipped > 0 || importResults.failed > 0) && (
            <Table
              dataSource={importResults.results.filter(r => r.status !== 'imported')}
              columns={resultsColumns}
              rowKey="row"
              size="small"
              scroll={{ y: 200 }}
              pagination={false}
            />
          )}
        </Space>
      </div>
    );
  };

  const renderFooter = () => {
    switch (currentStep) {
      case 'upload':
        return [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
        ];
      case 'preview':
        return [
          <Button key="back" onClick={() => setCurrentStep('upload')}>
            Back
          </Button>,
          <Button key="import" type="primary" onClick={handleImport} disabled={validRowsCount === 0}>
            Import {validRowsCount} Users
          </Button>,
        ];
      case 'importing':
        return [];
      case 'results':
        return [
          <Button key="done" type="primary" onClick={handleDone}>
            Done
          </Button>,
        ];
      default:
        return [];
    }
  };

  return (
    <Modal
      title="Import Users from CSV"
      open={open}
      onCancel={currentStep === 'importing' ? undefined : handleClose}
      footer={renderFooter()}
      width={900}
      closable={currentStep !== 'importing'}
      maskClosable={currentStep !== 'importing'}
    >
      <Steps
        current={stepIndex}
        items={[
          { title: 'Upload', icon: <UploadOutlined /> },
          { title: 'Preview', icon: <ExclamationCircleOutlined /> },
          { title: 'Import', icon: <CheckCircleOutlined /> },
          { title: 'Results', icon: <CheckCircleOutlined /> },
        ]}
        style={{ marginBottom: 24 }}
      />

      {currentStep === 'upload' && renderUploadStep()}
      {currentStep === 'preview' && renderPreviewStep()}
      {currentStep === 'importing' && renderImportingStep()}
      {currentStep === 'results' && renderResultsStep()}
    </Modal>
  );
}
